<template>
  <div class="view-sales-subscriptions">
    <div class="sales-header mb-4">
      <h4 class="text-semibold">Gestão de assinaturas</h4>
      <snow-button
        class="d-none d-lg-flex"
        variant="outline"
        size="md"
        right-icon="ph ph-arrow-square-out"
        text="Ir para Assinaturas"
        @click="$router.push({ name: 'sales-subscriptions' })"
      />
      <snow-button
        class="d-lg-none"
        variant="outline"
        size="md"
        icon="ph ph-arrow-square-out"
        @click="$router.push({ name: 'sales-subscriptions' })"
      />
    </div>
    <div>
      <div class="row mb-5">
        <div class="col-md mb-5 mb-lg-0">
          <info-card
            prefix=""
            title="Ativas"
            icon="ph ph-check-square-offset"
            :value="Math.round(overview.total_enable.tax) || 0"
          />
        </div>
        <div class="col-md mb-5 mb-lg-0">
          <info-card
            prefix=""
            title="Atrasadas"
            icon="ph ph-clock-countdown"
            :value="overview.total_past_due.count || 0"
          />
        </div>
        <div class="col-md mb-5 mb-lg-0">
          <info-card
            prefix=""
            title="Canceladas"
            icon="ph ph-x-circle"
            :value="overview.total_disable.count || 0"
          />
        </div>
      </div>

      <snow-data-table-header
        @open-filters="filtersModal = true"
        @searching="searching"
        :exportOptions="exportOptions"
        :add="false"
        :date-picker="false"
      />

      <div class="my-9 text-center" v-if="loading">
        <b-spinner variant="primary" />
      </div>

      <bp-data-table
        v-else
        main="status"
        :columns="columns"
        :data="subscriptions"
      >
        <template #comprador="{item}">
          <div>
            <div class="functional-1">
              {{ item.name }}
            </div>
            <div class="functional-2 text-neutral-400">
              {{ item.shopper_email }}
            </div>
          </div>
        </template>

        <template #produto="{value,item}">
          <div>
            <div class="functional-1">
              {{ value.name }}
            </div>
            <div v-if="item.plan" class="functional-2 text-neutral-400">
              {{ item.plan.name }}
            </div>
          </div>
        </template>

        <template v-slot:ciclo-atual="{ value, item }">
          <div class="d-flex">
            {{ value }}
            <bp-tooltip
              v-if="
                !item.cycles || item.cycles == 'Renova até o cliente cancelar!'
              "
              text="Renova até o cliente cancelar"
            >
              <i class="fa-regular fa-infinity ml-2" />
            </bp-tooltip>
          </div>
        </template>

        <template #status="{value,item}">
          <div :class="formatStatusBadge(value)">
            {{ formatStatusName(value) }}
          </div>
          <div
            class="functional-2 text-neutral-400"
            v-if="value !== 'enable' && item.cancel_signature_at"
          >
            Desde
            {{ new Date(item.cancel_signature_at).toLocaleDateString() }}
          </div>
        </template>
      </bp-data-table>

      <div class="d-flex justify-content-end mt-5" v-if="subscriptions.length">
        <snow-paginator
          v-show="!loading"
          v-model="currentPage"
          :pagination="pagination"
          @change-page="dispatch"
        />
      </div>
    </div>

    <responsive-modal
      v-model="filtersModal"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <subscription-filter
        v-model="filters"
        @close="filtersModal = false"
        @input="() => dispatch()"
      />
    </responsive-modal>
  </div>
</template>

<script>
import SubscriptionFilter from "@/components/sale/SubscriptionFilter.vue";
import {
  PERIODICITY_NAME_MAP,
  PERIODICITY_NUMBER_MAP,
  SALE_PAY_METHOD_NAME_MAP,
  SUBSCRIPTION_STATUS_BADGE_MAP,
  SUBSCRIPTION_STATUS_NAME_MAP
} from "@/constants/sales.js";
import { formatMoney } from "@/functions/helpers.js";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  components: {
    SubscriptionFilter
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      filtersModal: false,
      filters: {
        product_id: "",
        status: []
      },
      search: "",
      exportOptions: {
        action: "subscriptions/getCSV",
        filters: this.filters,
        filename: "subscriptions"
      }
    };
  },
  mounted() {
    this.dispatch();
    this.$store.dispatch("product/get", { limit: 1000 }).then();
  },
  computed: {
    ...mapState("subscriptions", ["subscriptions", "overview", "pagination"]),
    ...mapState("product", ["products"]),
    columns() {
      return [
        {
          name: `ID da assinatura`,
          field: "id",
          size: "0.6fr"
        },
        {
          name: "Comprador",
          field: "shopper_email"
        },
        {
          name: "Produto",
          field: "product"
        },
        {
          name: "Valor",
          field: "amount",
          transform: v => `R$ ${formatMoney(v)}`,
          size: "0.6fr"
        },
        {
          name: "Periodicidade",
          field: "frequency",
          size: "0.8fr"
        },
        {
          name: "Ciclo atual",
          field: "current_cycle",
          size: "0.6fr",
          transform: this.formatCurrentCycle
        },
        {
          name: "Pagamento",
          field: "payment_method",
          transform: v => SALE_PAY_METHOD_NAME_MAP[v]
        },
        {
          name: "Início",
          field: "created_at",
          transform: v => new Date(v).toLocaleDateString("pt-BR"),
          size: "0.6fr"
        },
        {
          name: "Status",
          field: "status"
        },
        {
          name: "",
          type: "menu",
          options: [
            {
              label: "Cancelar assinatura",
              icon: "icon-x font-weight-bold",
              class: "text-danger",
              variant: "danger",
              dialog: {
                text: `Deseja mesmo realizar o cancelamento da assinatura?`,
                variant: "negative",
                icon: "fa fa-hexagon-exclamation",
                secondaryAction: this.submitDelete,
                buttonText: "Cancelar",
                secondaryButtonText: "Confirmar"
              }
            },
            {
              label: "Bloquear acesso",
              icon: "fa-light fa-lock",
              dialog: {
                text: "Tem certeza que deseja bloquear o acesso?",
                variant: "negative",
                icon: "fa fa-hexagon-exclamation",
                secondaryAction: this.submitBlock,
                buttonText: "Cancelar",
                secondaryButtonText: "Confirmar"
              }
            }
          ]
        }
      ];
    }
  },
  methods: {
    refresh() {
      this.search = "";
      this.dispatch();
    },
    catchHandler(error) {
      if (error.response?.data?.details) {
        this.$message.error(error.response.data.details);
      }
    },
    searching(search) {
      this.search = search;
      this.dispatch();
    },
    dispatch(offset = 0) {
      this.loading = true;

      if (!offset) this.currentPage = 1;

      const params = {
        status: this.filters.status,
        product_id: this.filters.product_id,
        search: this.search,
        offset
      };

      Promise.all([
        this.$store.dispatch("subscriptions/getOverview", params),
        this.$store.dispatch("subscriptions/get", params)
      ])
        .catch(this.catchHandler)
        .finally(() => (this.loading = false));
    },
    submitBlock(item) {
      const params = {
        email: item.shopper_email,
        product_id: item.product.id
      };
      this.$store
        .dispatch("membersStudents/disableSubscription", params)
        .then(() => {
          this.$message.success("Acesso bloqueado.");
          this.dispatch();
        })
        .catch(error => {
          this.$message.error(
            get(
              error,
              "response.data.details",
              "Erro ao realizar o bloqueio do acesso."
            )
          );
        });
    },
    submitDelete(item) {
      this.$store
        .dispatch("subscriptions/delete", item.id)
        .then(() => {
          this.$message.success("Assinatura cancelada.");
          this.dispatch();
        })
        .catch(error => {
          this.$message.error(
            get(
              error,
              "response.data.details",
              "Erro ao realizar cancelamento."
            )
          );
        });
    },

    formatPeriodicity(periodicity) {
      return PERIODICITY_NAME_MAP[periodicity];
    },
    formatPeriodicityNumber(periodicity) {
      return PERIODICITY_NUMBER_MAP[periodicity];
    },
    formatStatusName(status) {
      return SUBSCRIPTION_STATUS_NAME_MAP[status];
    },
    formatStatusBadge(status) {
      return SUBSCRIPTION_STATUS_BADGE_MAP[status];
    },
    formatCurrentCycle(_, item) {
      if (!item.current_cycle) return "-";

      let value = item.current_cycle + " de ";
      if (item.cycles && item.cycles != "Renova até o cliente cancelar!")
        value += item.cycles;
      return value;
    }
  }
};
</script>
<style lang="scss" scoped>
.view-sales-subscriptions {
  .sales-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
